import React, { useEffect, useState } from "react"
import { useParams, NavLink } from "react-router-dom"
import moment from "moment"

import { db } from "../../utils/firebase"
import { AMENDMENT_CATEGORIES, AMENDMENT_STATUSES } from "../../utils/exports/amendments"
import { logActivity } from "../enquiry/_components/activity/log"
import ActivityFeed from "../enquiry/_components/activity/feed"
import { BOOKING_QUICK_ACTION_TYPES } from "../enquiry/_components/activity/types"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Toggle from "../../components/ui/toggle/toggle"
import Textarea from "../../components/ui/inputs/textarea"
import Button from "../../components/ui/button/button"

function useAmendment(amendmentID) {
	const [amendment, setAmendment] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!amendmentID) return
		const unsub = db.doc(`amendments/${amendmentID}`).onSnapshot((doc) => {
			setAmendment(doc.exists ? { id: doc.id, ...doc.data() } : null)
			setLoading(false)
		})
		return () => unsub()
	}, [amendmentID])

	return { amendment, loading }
}

function useBooking(bookingId) {
	const [booking, setBooking] = useState(null)

	useEffect(() => {
		if (!bookingId) return
		const unsub = db.doc(`bookings/${bookingId}`).onSnapshot((doc) => {
			setBooking(doc.exists ? { id: doc.id, ...doc.data() } : null)
		})
		return () => unsub()
	}, [bookingId])

	return booking
}

function useStaffOptions() {
	const [options, setOptions] = useState({})

	useEffect(() => {
		db.collection("users")
			.where("is_staff", "==", true)
			.get()
			.then((snap) => {
				const next = { unassigned: "Unassigned" }
				snap.docs.forEach((d) => {
					const { first_name, last_name } = d.data()
					const name = `${first_name || ""} ${last_name || ""}`.trim()
					if (name) next[d.id] = name
				})
				setOptions(next)
			})
			.catch((err) => console.error("Unable to load staff list", err))
	}, [])

	return options
}

function BookingSummary({ booking, fallbackReference }) {
	if (!booking) {
		return (
			<div className="amendment-card">
				<h3>Booking</h3>
				<p>
					<small>Loading booking…</small>
				</p>
			</div>
		)
	}

	const reference = booking.reference ? `GHDB-${booking.reference}` : booking.id
	const dateFrom = booking.date_from?.seconds
		? moment(booking.date_from.seconds, "X").format("MMM D, YYYY")
		: "—"
	const clientName = [booking.first_name, booking.last_name].filter(Boolean).join(" ")
	const location = booking.booked_location || booking.enquiry?.location || "—"

	return (
		<div className="amendment-card">
			<h3>Booking</h3>
			<p>
				<strong>
					<NavLink to={`/booking/${booking.id}`}>{reference || fallbackReference}</NavLink>
				</strong>
			</p>
			<p>{clientName || booking.email || "—"}</p>
			<p>
				{location} · {dateFrom}
			</p>
		</div>
	)
}

export default function Amendment() {
	const { amendmentID } = useParams()
	const { amendment, loading } = useAmendment(amendmentID)
	const booking = useBooking(amendment?.bookingId)
	const staffOptions = useStaffOptions()

	const [summaryDraft, setSummaryDraft] = useState("")
	const [summaryDirty, setSummaryDirty] = useState(false)
	const [savingSummary, setSavingSummary] = useState(false)

	useEffect(() => {
		if (!amendment) return
		setSummaryDraft(amendment.summary || "")
		setSummaryDirty(false)
	}, [amendment?.id, amendment?.summary])

	if (loading) {
		return (
			<Tile fullPage={true}>
				<Title>
					<h1>Amendment</h1>
				</Title>
				<p>
					<small>Loading…</small>
				</p>
			</Tile>
		)
	}

	if (!amendment) {
		return (
			<Tile fullPage={true}>
				<Title>
					<h1>Amendment not found</h1>
				</Title>
				<p>
					<NavLink to="/amendments">Back to amendments</NavLink>
				</p>
			</Tile>
		)
	}

	const basePath = `amendments/${amendment.id}`

	const handleStatusChange = async (option) => {
		const next = option.option
		if (next === amendment.status) return
		await db.doc(basePath).update({
			status: next,
			resolvedAt: next === "open" ? null : new Date(),
		})
		await logActivity(
			basePath,
			"STATUS_CHANGED",
			`Status changed from ${AMENDMENT_STATUSES[amendment.status] || amendment.status} to ${AMENDMENT_STATUSES[next] || next}`,
		)
	}

	const handleAwaitingClientToggle = async () => {
		const next = !amendment.awaitingClient
		await db.doc(basePath).update({ awaitingClient: next })
		await logActivity(
			basePath,
			"AMENDMENT_FLAG_CHANGED",
			next ? "Marked awaiting client" : "Cleared awaiting client",
		)
	}

	const handleAwaitingSupplierToggle = async () => {
		const next = !amendment.awaitingSupplier
		await db.doc(basePath).update({ awaitingSupplier: next })
		await logActivity(
			basePath,
			"AMENDMENT_FLAG_CHANGED",
			next ? "Marked awaiting supplier" : "Cleared awaiting supplier",
		)
	}

	const handleAssigneeChange = async (option) => {
		const next = option.option === "unassigned" ? null : option.option
		if (next === amendment.assignedTo) return
		const fromName = amendment.assignedTo
			? staffOptions[amendment.assignedTo] || "someone"
			: "Unassigned"
		const toName = next ? staffOptions[next] || "someone" : "Unassigned"
		await db.doc(basePath).update({ assignedTo: next })
		await logActivity(basePath, "AGENT_REASSIGNED", `Reassigned from ${fromName} to ${toName}`)
	}

	const handleSummarySave = async () => {
		if (!summaryDirty) return
		const trimmed = summaryDraft.trim()
		if (!trimmed) return
		setSavingSummary(true)
		await db.doc(basePath).update({ summary: trimmed })
		await logActivity(basePath, "NOTE", `Summary updated: ${trimmed}`)
		setSavingSummary(false)
		setSummaryDirty(false)
	}

	const categoryLabel =
		AMENDMENT_CATEGORIES[amendment.category] || amendment.category || "—"
	const categorySuffix =
		amendment.category === "other" && amendment.categoryOther
			? ` — ${amendment.categoryOther}`
			: ""

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>{amendment.reference}</h1>
				<NavLink
					to="/amendments"
					className="amendment-back-link">
					← Back to amendments
				</NavLink>
			</Title>

			<div className="amendment-grid">
				<BookingSummary
					booking={booking}
					fallbackReference={amendment.bookingReference}
				/>

				<div className="amendment-card">
					<h3>Status</h3>
					<Select
						placeholder="Status"
						value={amendment.status}
						selected={amendment.status}
						onSelect={handleStatusChange}
						options={AMENDMENT_STATUSES}
					/>

					<div className="amendment-flags">
						<Toggle
							label="Awaiting client"
							on={!!amendment.awaitingClient}
							onClick={handleAwaitingClientToggle}
						/>
						<Toggle
							label="Awaiting supplier"
							on={!!amendment.awaitingSupplier}
							onClick={handleAwaitingSupplierToggle}
						/>
					</div>
				</div>

				<div className="amendment-card">
					<h3>Assignee</h3>
					<Select
						placeholder="Assignee"
						value={amendment.assignedTo || "unassigned"}
						selected={amendment.assignedTo || "unassigned"}
						onSelect={handleAssigneeChange}
						options={staffOptions}
					/>
				</div>

				<div className="amendment-card amendment-card-wide">
					<h3>Category</h3>
					<p>
						{categoryLabel}
						{categorySuffix}
					</p>
				</div>

				<div className="amendment-card amendment-card-wide">
					<h3>Summary</h3>
					<Textarea
						placeholder="Short summary of the change"
						value={summaryDraft}
						onChange={(value) => {
							setSummaryDraft(value)
							setSummaryDirty(value !== (amendment.summary || ""))
						}}
					/>
					{summaryDirty && (
						<Button
							small={true}
							label="Save summary"
							loading={savingSummary}
							loadingText="Saving…"
							onClick={handleSummarySave}
							disabled={!summaryDraft.trim() || savingSummary}
						/>
					)}
				</div>
			</div>

			<div className="amendment-feed-wrap">
				<ActivityFeed
					basePath={basePath}
					quickActionTypes={BOOKING_QUICK_ACTION_TYPES}
					showAllTypes={true}
					emptyMessage="No activity yet — log a note or use a quick action above."
				/>
			</div>
		</Tile>
	)
}
